import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import API from "../../api/api";

export default function AdminMatrixReconcile() {
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [healingAll, setHealingAll] = useState(false);
  const [healingUserIds, setHealingUserIds] = useState(new Set());
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [data, setData] = useState({
    total_users_scanned: 0,
    balanced_count: 0,
    mismatches_count: 0,
    total_five_seats: 0,
    total_three_seats: 0,
    system_difference: 0,
    mismatches: [],
  });

  const loadScan = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/admin/matrix/reconcile-scan/", {
        params: { q, show_all: showAll ? "1" : "0" },
      });
      setData(res?.data || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to scan matrix accounts");
    } finally {
      setLoading(false);
    }
  }, [q, showAll]);

  useEffect(() => {
    loadScan();
  }, [loadScan]);

  const handleHealUser = async (userId, username) => {
    try {
      setHealingUserIds((prev) => new Set(prev).add(userId));
      setErr("");
      setMsg("");
      const res = await API.post("/admin/matrix/reconcile-heal/", { user_id: userId });
      const count = res?.data?.total_healed || 0;
      setMsg(`Successfully healed ${count} missing seat(s) for user ${username}!`);
      await loadScan();
    } catch (e) {
      setErr(e?.response?.data?.detail || `Failed to heal user ${username}`);
    } finally {
      setHealingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleHealAll = async () => {
    if (!window.confirm(`Are you sure you want to reconcile all ${data.mismatches_count} mismatched members?`)) {
      return;
    }
    try {
      setHealingAll(true);
      setErr("");
      setMsg("");
      const res = await API.post("/admin/matrix/reconcile-heal/", { all: true });
      const count = res?.data?.total_healed || 0;
      const affected = res?.data?.users_affected || 0;
      setMsg(`Successfully healed ${count} seats across ${affected} members!`);
      await loadScan();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to reconcile all accounts");
    } finally {
      setHealingAll(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* Page Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: "#0F172A", fontSize: { xs: 18, sm: 22 } }}>
            🛡️ Matrix Reconciler & Self-Healing
          </Typography>
          <Typography sx={{ color: "#64748B", fontSize: { xs: 12, sm: 13 }, mt: 0.3 }}>
            Inspect, verify, and heal 5-Matrix vs 3-Matrix Rebirth seat pairing across all members.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <Button
            variant="outlined"
            onClick={loadScan}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon />}
            sx={{
              flex: { xs: 1, sm: "initial" },
              borderRadius: 2,
              fontWeight: 700,
              fontSize: { xs: 12.5, sm: 14 },
              py: { xs: 0.8, sm: 1 },
              textTransform: "none",
            }}
          >
            Scan System
          </Button>
          {data.mismatches_count > 0 && (
            <Button
              variant="contained"
              onClick={handleHealAll}
              disabled={healingAll || loading}
              startIcon={healingAll ? <CircularProgress size={16} color="inherit" /> : <BuildRoundedIcon />}
              sx={{
                flex: { xs: 1, sm: "initial" },
                borderRadius: 2,
                fontWeight: 800,
                fontSize: { xs: 12.5, sm: 14 },
                py: { xs: 0.8, sm: 1 },
                textTransform: "none",
                bgcolor: "#0284C7",
                "&:hover": { bgcolor: "#0369A1" },
              }}
            >
              {healingAll ? "Healing..." : `Reconcile All (${data.mismatches_count})`}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Metric Cards - 2x2 on mobile, 4 in row on desktop */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: { xs: 1.2, sm: 2 },
          mb: 2,
        }}
      >
        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: "1px solid #E2E8F0", bgcolor: "#FFFFFF" }}>
          <Typography sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
            Total Users
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 900, color: "#0F172A", mt: 0.3 }}>
            {data.total_users_scanned}
          </Typography>
          <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#059669", fontWeight: 700, mt: 0.2 }}>
            {data.balanced_count} Balanced
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: "1px solid #E2E8F0", bgcolor: "#FFFFFF" }}>
          <Typography sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
            5-Matrix Seats
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 900, color: "#0284C7", mt: 0.3 }}>
            {data.total_five_seats}
          </Typography>
          <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#64748B", mt: 0.2 }}>
            Pool: FIVE_150
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: "1px solid #E2E8F0", bgcolor: "#FFFFFF" }}>
          <Typography sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
            3-Matrix Seats
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 900, color: "#8B5CF6", mt: 0.3 }}>
            {data.total_three_seats}
          </Typography>
          <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#64748B", mt: 0.2 }}>
            Pool: THREE_150
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: data.mismatches_count > 0 ? "#FCA5A5" : "#A7F3D0",
            bgcolor: data.mismatches_count > 0 ? "#FEF2F2" : "#ECFDF5",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 10, sm: 11.5 },
              fontWeight: 700,
              color: data.mismatches_count > 0 ? "#DC2626" : "#059669",
              textTransform: "uppercase",
            }}
          >
            Mismatches
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 20, sm: 24 },
              fontWeight: 900,
              color: data.mismatches_count > 0 ? "#DC2626" : "#059669",
              mt: 0.3,
            }}
          >
            {data.mismatches_count}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 10, sm: 11 },
              fontWeight: 700,
              color: data.mismatches_count > 0 ? "#B91C1C" : "#047857",
              mt: 0.2,
            }}
          >
            {data.mismatches_count > 0 ? "Action Required" : "100% Balanced"}
          </Typography>
        </Paper>
      </Box>

      {/* Messages */}
      {err && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{msg}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Filter Card */}
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2.5, border: "1px solid #E2E8F0" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <TextField
            size="small"
            fullWidth
            placeholder="Search phone, username, or name..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                loadScan();
              }
            }}
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ color: "#94A3B8", mr: 1, fontSize: 18 }} />,
            }}
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              "& .MuiInputBase-input": { fontSize: { xs: 13, sm: 14 } },
            }}
          />

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, whiteSpace: "nowrap" }}>
                  Show Balanced
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Button
              variant="contained"
              onClick={loadScan}
              size="small"
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
                px: { xs: 2.5, sm: 3 },
                py: { xs: 0.7, sm: 0.9 },
                fontSize: { xs: 12.5, sm: 13.5 },
              }}
            >
              Search
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Results Header */}
      <Typography sx={{ fontSize: { xs: 14.5, sm: 16 }, fontWeight: 900, color: "#0F172A", mb: 1.5 }}>
        {showAll ? "All Member Accounts" : "Unpaired Mismatch Queue"} ({data.mismatches?.length || 0})
      </Typography>

      {/* Mismatch List */}
      <Stack spacing={1.5}>
        {data.mismatches && data.mismatches.length > 0 ? (
          data.mismatches.map((r) => {
            const isUserHealing = healingUserIds.has(r.user_id);
            return (
              <Paper
                key={r.user_id}
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: r.is_balanced ? "#E2E8F0" : "#FCA5A5",
                  bgcolor: r.is_balanced ? "#FFFFFF" : "#FFFBFB",
                  transition: "all 150ms ease",
                }}
              >
                {/* Header row: Member Name + Status Chip */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: 14.5, sm: 15.5 }, color: "#0F172A", lineHeight: 1.2 }}>
                    {r.full_name || r.username}
                  </Typography>
                  {!r.is_balanced ? (
                    <Chip
                      icon={<WarningRoundedIcon sx={{ fontSize: "13px !important" }} />}
                      label="Unpaired Seat"
                      size="small"
                      color="error"
                      sx={{ fontWeight: 800, fontSize: 10.5, height: 22 }}
                    />
                  ) : (
                    <Chip
                      icon={<CheckCircleRoundedIcon sx={{ fontSize: "13px !important" }} />}
                      label="Balanced"
                      size="small"
                      color="success"
                      sx={{ fontWeight: 800, fontSize: 10.5, height: 22 }}
                    />
                  )}
                </Stack>

                {/* Subtitle: Phone & User ID */}
                <Typography sx={{ color: "#64748B", fontSize: { xs: 11.5, sm: 12 }, mb: 1.2 }}>
                  Phone: <strong>{r.phone}</strong> • User ID: #{r.user_id}
                </Typography>

                {/* Middle Row: Seat Counts Bar & Missing Pack Details */}
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  spacing={1.2}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.8,
                        bgcolor: "#F0F9FF",
                        border: "1px solid #BAE6FD",
                        borderRadius: 1.5,
                        px: 1.2,
                        py: 0.4,
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#0369A1", fontWeight: 700 }}>5-Blocks:</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0284C7" }}>{r.five_count} IDs</Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.8,
                        bgcolor: "#F5F3FF",
                        border: "1px solid #DDD6FE",
                        borderRadius: 1.5,
                        px: 1.2,
                        py: 0.4,
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#6D28D9", fontWeight: 700 }}>3-Blocks:</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#8B5CF6" }}>{r.three_count} IDs</Typography>
                    </Box>

                    {/* Missing Packs Info */}
                    {r.missing_in_three && r.missing_in_three.length > 0 && (
                      <Chip
                        label={`Missing in 3M: Pack #${r.missing_in_three.join(", #")}`}
                        size="small"
                        sx={{
                          bgcolor: "#FEE2E2",
                          color: "#991B1B",
                          fontWeight: 700,
                          fontSize: 11,
                          height: 24,
                          maxWidth: "100%",
                          "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                        }}
                      />
                    )}
                    {r.missing_in_five && r.missing_in_five.length > 0 && (
                      <Chip
                        label={`Missing in 5M: Pack #${r.missing_in_five.join(", #")}`}
                        size="small"
                        sx={{
                          bgcolor: "#FEE2E2",
                          color: "#991B1B",
                          fontWeight: 700,
                          fontSize: 11,
                          height: 24,
                          maxWidth: "100%",
                          "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                        }}
                      />
                    )}
                  </Stack>

                  {/* Action Button: Full width on mobile, right-aligned on desktop */}
                  {!r.is_balanced ? (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={isUserHealing || healingAll}
                      onClick={() => handleHealUser(r.user_id, r.username)}
                      startIcon={isUserHealing ? <CircularProgress size={14} color="inherit" /> : <BuildRoundedIcon />}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 800,
                        fontSize: { xs: 12.5, sm: 12 },
                        textTransform: "none",
                        bgcolor: "#DC2626",
                        "&:hover": { bgcolor: "#B91C1C" },
                        width: { xs: "100%", md: "auto" },
                        minWidth: 110,
                        py: { xs: 0.8, md: 0.6 },
                        mt: { xs: 0.5, md: 0 },
                      }}
                    >
                      {isUserHealing ? "Healing..." : "Heal User"}
                    </Button>
                  ) : (
                    <Chip
                      label="In Perfect Sync"
                      size="small"
                      sx={{
                        bgcolor: "#ECFDF5",
                        color: "#065F46",
                        fontWeight: 700,
                        alignSelf: { xs: "flex-start", md: "center" },
                      }}
                    />
                  )}
                </Stack>
              </Paper>
            );
          })
        ) : (
          <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px dashed #CBD5E1", bgcolor: "#F8FAFC" }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 44, color: "#059669", mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#0F172A" }}>
              All Rebirth Seats Are 100% Balanced!
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.5 }}>
              No unpaired 5-Matrix or 3-Matrix rebirth seats found in the system.
            </Typography>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
